<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" role="dialog" aria-modal="true" @click="closeModal">
      <div class="modal-content form-modal" @click.stop>
        <div class="modal-body">
          <div class="form-header">
            <button @click="closeModal" class="close-button">&times;</button>
            <h2>Isotope Enquiry</h2>
          </div>
          
          <form @submit.prevent="onSubmit" class="grid pt-4">
            <div class="col-12 mb-1">
              <h2 class="m-0">Your Details:</h2>
            </div>
            <div class="col-6">
              <label for="name" class="hidden mb-2">Name *</label>
              <InputText id="name" placeholder="Name*" v-model="form.name" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="phone" class="hidden mb-2">Phone *</label>
              <InputText id="phone" placeholder="Phone number*" v-model="form.phone" type="phone" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="email" class="hidden mb-2">Email *</label>
              <InputText id="email" placeholder="Email address*" v-model="form.email" type="email" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="post_address" class="hidden mb-2">Postal Address *</label>
              <InputText id="post_address" placeholder="Postal address*" v-model="form.post_address" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="organisation" class="hidden mb-2">Organisation *</label>
              <InputText id="organisation" placeholder="Organisation*" v-model="form.organisation" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="isotope_form" class="hidden mb-2">What form of Isotope? *</label>
              <InputText id="isotope_form" placeholder="Form of isotope*" v-model="form.isotope_form" disabled="disabled" required class="w-full" />
            </div>
            <div class="col-6">
              <label for="quantity" class="hidden mb-2">Quantity</label>
              <InputText id="quantity" placeholder="Quantity" v-model="form.quantity" type="number" min="1" class="w-full" />
            </div>
            <div class="col-6">
              <label for="chemical_form" class="hidden mb-2">Chemical form</label>
              <InputText id="chemical_form" placeholder="Chemical form*" v-model="form.chemical_form" class="w-full" />
            </div>
            <div class="col-12">
              <label for="application" class="hidden mb-2">Application</label>
              <InputText id="application" placeholder="Application*" v-model="form.application" class="w-full" />
            </div>
            <div class="col-6 mb-1">
              <label for="additional_demands_for_chemical_purity" class="mb-2">Additional demands for purity</label>
              <InputText id="additional_demands_for_chemical_purity" v-model="form.additional_demands_for_chemical_purity" class="w-full" />
            </div>
            <div class="col-6 mb-1">
              <label for="enrichment_level" class="mb-2">What Enrichment level? *</label>
              <InputText id="enrichment_level" v-model="form.enrichment_level" required class="w-full" />
            </div>
            <div class="col-12 mb-1">
              <label for="additional_questions" class="mb-2">Additional questions *</label>
              <Textarea id="additional_questions" rows="4" cols="30" style="resize: none"  v-model="form.additional_questions" class="w-full" />
            </div>
            
            <input type="hidden" v-model="form.referral_url" />
            <input type="hidden" v-model="form.recaptcha" />
            
            <div class="col-12">
              <Button type="submit" label="Submit Enquiry" class="w-full" :disabled="loading" />
            </div>
            <div class="col-12 flex align-items-top">
              <Checkbox id="marketing_source" v-model="form.marketing_source" :binary="true" />
              <label for="marketing_source" class="ml-2">By submitting this form, you agree to allow Urenco Ltd to store and process your contact details. Tick this box if you would like to opt in to receive marketing content.</label>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Toast for notifications -->
    <Toast position="top-right" />
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, reactive, ref, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import { useToast } from '../composables/useToast.js';
import OfflineSubmissionService from '../services/OfflineSubmissionService.js';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  selectedIsotope: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close']);
const { showSuccess, showError } = useToast();
const loading = ref(false);

const form = reactive({
  name: '',
  phone: '',
  email: '',
  post_address: '',
  organisation: '',
  isotope_form: '',
  quantity: '',
  chemical_form: '',
  application: '',
  additional_demands_for_chemical_purity: '',
  enrichment_level: '',
  additional_questions: '',
  referral_url: window.location.href,
  marketing_source: false,
  recaptcha: ''
});

// Watch for changes in selectedIsotope and pre-populate the isotope_form field
watch(() => props.selectedIsotope, (newIsotope) => {
  if (newIsotope) {
    form.isotope_form = 'Enquiring about ' + newIsotope;
  }
}, { immediate: true });

const closeModal = () => {
  emit('close');
};

const resetForm = () => {
  Object.keys(form).forEach(key => {
    if (key === 'referral_url') {
      form[key] = window.location.href;
    } else if (key === 'marketing_source') {
      form[key] = false;
    } else if (key === 'isotope_form' && props.selectedIsotope) {
      form[key] = props.selectedIsotope; // Keep the pre-populated isotope
    } else {
      form[key] = '';
    }
  });
};

const onSubmit = async () => {
  loading.value = true;
  try {
    const recaptchaClient = window.grecaptcha;
    if (navigator.onLine && recaptchaClient?.ready && recaptchaClient?.execute) {
      const siteKey = window.AppConfig.recaptchaSiteKey;
      await new Promise((resolve) => recaptchaClient.ready(resolve));
      form.recaptcha = await recaptchaClient.execute(siteKey, { action: 'submit' });
    } else {
      form.recaptcha = '';
    }
    
    // Format the enquiry to include isotope-specific information
    const formattedEnquiry = `Isotope Enquiry
Organisation: ${form.organisation}
Isotope Form: ${form.isotope_form}
Enrichment Level: ${form.enrichment_level}
Quantity: ${form.quantity}
Chemical Form: ${form.chemical_form}
Application: ${form.application}
Additional Demands for Chemical Purity: ${form.additional_demands_for_chemical_purity}
Additional Questions: ${form.additional_questions}`;
    
    // Send form with token as 'g-recaptcha-response', mapping to existing API fields
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      post_address: form.post_address,
      enquiry: formattedEnquiry,
      referral_url: form.referral_url,
      marketing_source: form.marketing_source,
      'g-recaptcha-response': form.recaptcha
    };
    
    const result = await OfflineSubmissionService.submitWithOfflineSupport('/api/lead-submissions', payload);
    
    if (result.status === 'queued') {
      showSuccess('Saved offline. Will sync automatically when connected.');
    } else {
      showSuccess("Thank you! We've received your request and will get back to you as soon as possible. If you need immediate assistance, feel free to contact us directly via isotopes@urenco.com or +31 (0)546 545 151");
    }
    
    // Reset form after successful submission
    resetForm();
    
    // Close modal after successful submission
    setTimeout(() => {
      closeModal();
    }, 2000);
    
  } catch (error) {
    console.error(error);
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Submission failed. Please try again.';
    showError(errorMessage);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  overflow-y: auto;
  padding: max(84px, 8vh) 20px calc(32px + env(safe-area-inset-bottom, 0px));
}

.form-modal {
  max-width: 600px;
  width: 100%;
  max-height: none;
  overflow: visible;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 40px;
  background: white;
  border-radius: 20px;
}

.form-header {
  position: relative;
  text-align: center;
  margin-bottom: 30px;
}

.form-header h2 {
  margin: 0 0 10px 0;
  color: #7f73e6;
  font-size: 28px;
}

.form-header p {
  margin: 0;
  color: #666;
  font-size: 16px;
}

.close-button {
  position: absolute;
  top: -20px;
  right: -20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f73e6;
  padding: 10px;
  border-radius: 50%;
  border: 2px solid #000;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #333;
}

.grid .col-12 {
  margin-bottom: 20px;
}

.grid .col-12 label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.grid .col-12:last-child {
  margin-bottom: 0;
}

/* Ensure textarea accepts all input including spaces */
.p-inputtextarea {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
}

.p-inputtextarea:focus {
  outline: none !important;
  border-color: #7f73e6 !important;
}

@media (max-width: 767px) {
  .modal-overlay {
    padding: 10px 10px calc(24px + env(safe-area-inset-bottom, 0px));
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .form-header h2 {
    font-size: 24px;
  }
  
  .close-button {
    top: -10px;
    right: -10px;
  }
}
</style>
